import { BaseModel } from '@shared/models/base.model'

import { VehicleGroup } from './vehicle-group.model'
import { Client } from './client.model'
import { Team } from './team.model'
import { Depot } from './depot.model'
import { Device, DeviceStatus, RouteArea, TrackerData } from './device.model'
import { User } from './user.model'
import { Driver, TodayData } from './driver.model'
import { Coordinate } from './coordinate.model'
import { Asset } from '@shared/models/asset.model'
import mapboxgl from 'mapbox-gl'

export class AbstractVehicle extends BaseModel {
  id: number
  name: string
  client: Client
  depot: Depot
  type: VehicleType
  model: string
  device: Device
  deviceId: number
  available: string
  regNo: string
  defaultLabel: string
  vin: string
  regCertNo: string
  enginePower: string
  engineCapacity: string
  fuelType: string
  picture: string
  make: string
  makeModel: string
  emissionClass: string
  co2Emissions: string
  grossWeight: string
  status: VehicleStatus
  vehicleType: string
  vehicleNo: string
  driver: User
  vehicleTitle: string
  year: string
  averageFuel: string
  ecoSpeed: string
  vehicleReg: string
  mileage: string
  team: Team
  iconText?: string
  excessiveIdling: string
  engineHours: string
  fuelTankCapacity: string
  averageDailyMileage: string
  isUnavailable: boolean
  unavailableMessage: string
  clientNote: string
  groups: VehicleGroup[]
  areas: AreaHistory[]
  engineOnTime: number
  todayData: TodayData
  driverHistory: DriverHistory
  createdAt: string
  updatedAt: string
  createdByName?: string
  updatedBy: Pick<User, 'fullName' | 'id' | 'email' | 'teamType'>
  typeId: number
  typeName: string
  createdBy: Pick<User, 'fullName' | 'id' | 'email' | 'teamType'>
  socketUpdated?: number
  lastRoute?: VehicleRoute
  inScope?: boolean
}

export class Vehicle extends AbstractVehicle {
  regDate: string

  constructor(src?: Vehicle) {
    super(src)

    this.driver = this.driver && new User(this.driver)
    this.picture = this.picture && this.fileUrl + this.picture
    this.socketUpdated = src.socketUpdated || 0
  }
}

export type VehicleInList = Pick<Vehicle, 'id' | 'model' | 'team'>
export type VehicleTruncated = Pick<Vehicle, 'id' | 'regNo'> & Partial<Pick<Vehicle, 'mileage' | 'engineHours'>>

export class InputVehicle extends AbstractVehicle {
  regDate: string
  depotId: number
  teamId: number
}

export interface DriverHistory {
  id: string
  startDate: string
}

export interface AreaHistory {
  id: number
  area: Area
  arrived: string
  departed: string
  driverArrived: User
  driverDeparted: User
}

export interface Area {
  coordinates: Coordinate[]
  id: number
  name: string
  status: AreaStatus
  color: string
}

export enum AreaStatus {
  Active = 'active',
  Deleted = 'deleted',
}

export enum VehicleStatus {
  Online = 'online',
  Offline = 'offline',
  Unavailable = 'unavailable',
  Deleted = 'deleted',
  Archive = 'archive',
}

export enum VehicleType {
  Truck = 'Truck',
  Bus = 'Bus',
  Car = 'Car',
}

export class VehicleRoute extends BaseModel {
  id: number
  address: string
  duration: number
  indexStop: string
  coordinates: RouteCoordinate[]
  pointFinish: TrackerData
  distance: string
  pointStart: TrackerData
  type: DeviceStatus
  scope: Scope | null
  originalRoute?: Partial<VehicleRoute>
  startArea?: RouteArea[]
  finishArea?: RouteArea[]
  vehicleId?: number
  regNo?: string

  constructor(src?: VehicleRoute) {
    super(src)

    const getDeviceStatus = ({ movement, ignition }: RouteCoordinate) => {
      if (movement && ignition) return DeviceStatus.Driving
      if (!movement && ignition) return DeviceStatus.Idle
      return DeviceStatus.Stopped
    }
    const toRouteCoordinate = (routeCoordinate: RouteCoordinate) => ({
      ...routeCoordinate,
      lat: +routeCoordinate.lat,
      lng: +routeCoordinate.lng,
      status: getDeviceStatus(routeCoordinate),
    })

    this.coordinates = this.coordinates && this.coordinates.map(toRouteCoordinate)
    if (this.pointFinish) {
      this.pointFinish.lastCoordinates = {
        lat: +this.pointFinish.lastCoordinates.lat,
        lng: +this.pointFinish.lastCoordinates.lng,
        ts: this.pointFinish.lastCoordinates.ts,
      }
    }
  }
}

export interface RouteCoordinate {
  lat: number
  lng: number
  speed: number
  externalVoltage: number
  temperatureLevel: number
  ts: string
  ignition: number
  movement: number
  nullable: boolean
  status: DeviceStatus
}

export enum Scope {
  Private = 'private',
  Work = 'work',
  Uncategorised = 'uncategorised',
}

export enum TripCodes {
  HOME_PRIVATE = '1 To / From home / Miscellaneous private',
  TO_MEETING = '2 To meeting',
  FROM_MEETING = '3 Return Meeting',
  DEL_PICKUP = '4 Del / Pickup',
  ADMIN_NEPT = '5 Admin/ NEPT',
  REPAIRS = '6 Repairs',
}

export type VehicleDriver = Vehicle & Pick<Driver, 'picture' | 'fullName' | 'lastRoute' | 'todayData'> & { driverId: Driver['id'] }

export type VehicleMarker = Pick<Vehicle, 'defaultLabel' | 'regNo' | 'type' | 'id' | 'device' | 'typeId'> &
  Coordinate & {
    customVehicleType?: VehicleModelType
    asset?: Asset
    driverDetails?: Driver
    mapInstance?: mapboxgl.Marker | google.maps.Marker | L.Marker
  }

export const VehicleStatusLabel = {
  [VehicleStatus.Online]: 'CLIENT.VEHICLES.STATUSES.ONLINE',
  [VehicleStatus.Offline]: 'CLIENT.VEHICLES.STATUSES.OFFLINE',
  [VehicleStatus.Unavailable]: 'CLIENT.VEHICLES.STATUSES.UNAVAILABLE',
  [VehicleStatus.Archive]: 'CLIENT.VEHICLES.STATUSES.ARCHIVED',
} as const

export const VehicleStatusColor = {
  [VehicleStatus.Online]: '#4caf50',
  [VehicleStatus.Offline]: '#757575',
  [VehicleStatus.Unavailable]: '#ff9800',
  [VehicleStatus.Archive]: '#757575',
} as const

export const VehicleTypeLabel = {
  [VehicleType.Truck]: 'CLIENT.VEHICLES.TYPES.TRUCK',
  [VehicleType.Bus]: 'CLIENT.VEHICLES.TYPES.BUS',
  [VehicleType.Car]: 'CLIENT.VEHICLES.TYPES.CAR',
} as const

export interface Fuel {
  id: number
  name: FuelType
}

export interface FuelStation {
  id: number
  siteId: string
  stationName: string
  lat: string
  lng: string
  address: string
}

export enum FuelType {
  Diesel = 'Diesel',
  Petrol = 'Petrol',
  Petrol_Gasoline = 'Petrol/Gasoline',
  Gas = 'Gas',
  LPG = 'LPG',
  ElectricEngine = 'Electric engine',
}

export const FuelTypeLabel = {
  [FuelType.Diesel]: 'CLIENT.VEHICLES.FUEL_TYPES.DIESEL',
  [FuelType.Petrol]: 'CLIENT.VEHICLES.FUEL_TYPES.PETROL',
  [FuelType.Petrol_Gasoline]: 'CLIENT.VEHICLES.FUEL_TYPES.PETROL_GASOLINE',
  [FuelType.Gas]: 'CLIENT.VEHICLES.FUEL_TYPES.GAS',
  [FuelType.LPG]: 'CLIENT.VEHICLES.FUEL_TYPES.LPG',
  [FuelType.ElectricEngine]: 'CLIENT.VEHICLES.FUEL_TYPES.ELECTRIC_ENGINE',
}

export interface VehicleModelType {
  id: number
  name: string
  default: string | null
  driving: string | null
  idling: string | null
  stopped: string | null
  vehicleCount: number
  status: string
  order: number
}

export interface VehicleTypeImageUplaod {
  title: string
  picture: string
  translateValue: string
  defaultPicture: string
}

export interface VehicleModelTypeOrder {
  order: Pick<VehicleModelType, 'id' | 'order'>[]
}
